import { KubesoloHostResult, KubesoloStep } from '@api/types.gen';

import { describeCause } from './stepFailures';

const MACHINE_PLACEHOLDER = '<machine>';

export interface FailureCause {
  key: string;
  /** The failed step of the first machine, which explains the cause for all of them */
  step: KubesoloStep;
  title: string;
  /** For an error Portainer has no code for, the raw error with the machine's address taken out */
  unrecognisedError?: string;
  hosts: Array<KubesoloHostResult>;
}

/** Groups failed machines by step, code and values, ignoring values that name the machine; changed host keys first. */
export function groupByCause(
  hosts: Array<KubesoloHostResult>
): Array<FailureCause> {
  const groups = new Map<string, FailureCause>();

  hosts.forEach((host) => {
    const step = host.steps.find(({ state }) => state === 'failed');
    if (!step) {
      return;
    }

    const unrecognisedError = step.error
      ? undefined
      : withoutMachine(step.detail ?? '', host);
    const key = causeKey(host, step, unrecognisedError);
    const group = groups.get(key);
    if (group) {
      group.hosts.push(host);
      return;
    }

    groups.set(key, {
      key,
      step,
      title: describeCause(step),
      unrecognisedError,
      hosts: [host],
    });
  });

  return [...groups.values()].sort(
    (a, b) =>
      Number(isHostKeyChange(b)) - Number(isHostKeyChange(a)) ||
      b.hosts.length - a.hosts.length
  );
}

function causeKey(
  host: KubesoloHostResult,
  step: KubesoloStep,
  unrecognisedError?: string
) {
  if (unrecognisedError !== undefined) {
    return JSON.stringify([step.name, unrecognisedError]);
  }

  const code = step.error?.code;
  // every machine's fingerprints differ, and each machine shows its own
  const values =
    code === 'hostKeyChanged'
      ? []
      : Object.entries(step.error?.values ?? {})
          .filter(([, value]) => !value.includes(host.host))
          .sort(([a], [b]) => a.localeCompare(b));
  // kubesoloctl's failures differ only in their detail
  const cause = code === 'checkFailed' ? step.detail : code;

  return JSON.stringify([step.name, cause, values]);
}

/** Raw errors name the machine, so the same error on two machines reads the same once it is taken out. */
function withoutMachine(error: string, host: KubesoloHostResult) {
  return [
    `[${host.host}]:${host.port}`,
    `${host.host}:${host.port}`,
    host.host,
  ].reduce((text, name) => text.split(name).join(MACHINE_PLACEHOLDER), error);
}

function isHostKeyChange(cause: FailureCause) {
  return !!cause.hosts[0].hostKeyChange;
}
